import { CircularProgress, InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid2";
import cep from "cep-promise";
import { useFormikContext } from "formik";
import React, { useRef } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { SubscribeFormData } from "@/components/templates/SubscribeForm/types";
import { ADDRESS_MASK, CEP_LENGHT, STATE_UF_TRANSLATIONS } from "@/constants/documents.constants";
import { applyDocumentMask, determineDocumentType } from "@/helpers/document-helpers";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import useLoading from "@/hooks/use-loading";
import { SubscribeFormMessages } from "@/messages/subscribe/subscribe-form.messages";

type Props = {
  messages: SubscribeFormMessages;
};

const PersonalDataForm = ({ messages }: Props) => {
  const formik = useFormikContext<SubscribeFormData>();
  const { values, handleChange, errors, setFieldValue, setFieldError, initialValues } = formik;
  const { startLoading, endLoading, isLoading } = useLoading();

  const addressNumberRef = useRef<HTMLInputElement>(null);

  const clearData = () => {
    setFieldValue("state", "");
    setFieldValue("city", "");
    setFieldValue("neighborhood", "");
    setFieldValue("address", "");
  };

  const handleCEPChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = e;

    const formattedValue = removeNonIntegersFromString(value);

    setFieldError("zipCode", undefined);
    setFieldValue("zipCode", formattedValue);

    const isCEPCompleted = formattedValue.length === CEP_LENGHT;

    if (!isCEPCompleted) clearData();

    if (isCEPCompleted) {
      startLoading();
      try {
        const cepData = await cep(formattedValue);
        const addressState = STATE_UF_TRANSLATIONS[cepData.state];
        setFieldValue("state", addressState);
        setFieldValue("city", cepData.city);
        setFieldValue("neighborhood", cepData.neighborhood);
        setFieldValue("address", cepData.street);

        setTimeout(() => {
          addressNumberRef.current?.focus();
        }, 0);
      } catch (err) {
        setFieldError("zipCode", messages.cepNotFoundError);
        clearData();
      }
      endLoading();
    }
  };

  const addressMask = ADDRESS_MASK("BRASIL");

  const isCpfOrCnpj = determineDocumentType(values.document);
  const isInitialValue = values.document === initialValues.document;
  const disabledDocument = isCpfOrCnpj !== "UNDEFINED" && isInitialValue;

  return (
    <MDBox p={3}>
      <MDBox display="flex" justifyContent="space-between">
        <MDTypography variant="h5" fontWeight={"bold"}>
          {messages.yourDataTitle}
        </MDTypography>
      </MDBox>

      <MDBox mt={1}>
        <Grid container spacing={3}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormField
                variant="standard"
                name="name"
                label={messages.nameLabel}
                value={values.name}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.name}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <InputMaskWrapper
                mask={applyDocumentMask(values.document)}
                value={values.document as string}
                onChange={handleChange}
                disabled={disabledDocument}
                variant="standard"
                name="document"
                label={messages.documentLabel}
                fullWidth
                error={!!errors.document}
                helperText={errors.document}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <InputMaskWrapper
                mask={addressMask}
                value={values.zipCode}
                onChange={(e) => handleCEPChange(e)}
                required
                variant="standard"
                name="zipCode"
                label={messages.zipCodeLabel}
                fullWidth
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...(isLoading && {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CircularProgress size="small" />
                      </InputAdornment>
                    ),
                  }),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="address"
                label={messages.addressLabel}
                value={values.address}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.address}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="number"
                label={messages.numberLabel}
                value={values.number}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.number}
                inputRef={addressNumberRef}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="complement"
                label={messages.complementLabel}
                value={values.complement}
                onChange={handleChange}
                fullWidth
                error={!!errors.complement}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="neighborhood"
                label={messages.neighborhoodLabel}
                value={values.neighborhood}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.neighborhood}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="city"
                label={messages.cityLabel}
                value={values.city}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.city}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormField
                variant="standard"
                name="state"
                label={messages.stateLabel}
                value={values.state}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.state}
              />
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  );
};

export default PersonalDataForm;
